<div class="invite-container">
  <div class="invite-header">
    <button class="btn-back" (click)="onCancel()" title="Go back">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>Back</span>
    </button>
    <h1>Invite a Relative</h1>
    <p class="subtitle">Send an invitation to join your family</p>
  </div>

  <div class="invite-content">
    <form [formGroup]="inviteForm" (ngSubmit)="onSubmit()">
      <!-- Relationship Type -->
      <div class="form-group">
        <label for="relationshipType" class="form-label">
          Relationship Type <span class="required">*</span>
        </label>
        <select 
          id="relationshipType" 
          formControlName="relationshipType"
          class="form-control"
          [class.error]="inviteForm.get('relationshipType')?.invalid && inviteForm.get('relationshipType')?.touched">
          <option value="">Select relationship type</option>
          <option *ngFor="let type of relationshipTypes" [value]="type.value">
            {{ type.label }}
          </option>
        </select>
        <div *ngIf="inviteForm.get('relationshipType')?.invalid && inviteForm.get('relationshipType')?.touched" class="error-message">
          Please select a relationship type
        </div>
      </div>

      <!-- Invite Code -->
      <div class="form-group">
        <label for="inviteCode" class="form-label">
          Invite Code <span class="required">*</span>
        </label>
        
        <!-- For Admin: Show relative selector or manual input -->
        <div *ngIf="isAdmin" class="admin-invite-code">
          <div class="relative-selector" *ngIf="!showInviteCodeInput">
            <select 
              class="form-control"
              [ngModel]="selectedRelativeIndex"
              (ngModelChange)="onRelativeSelect($event)"
              [disabled]="isLoadingRelatives">
              <option [ngValue]="null">Select a relative or enter manually</option>
              <option *ngFor="let relative of relatives; let i = index" [ngValue]="i">
                {{ relative.firstName }} {{ relative.middleName || '' }} {{ relative.lastName }} ({{ relative.referralCode }})
              </option>
            </select>
            <button type="button" class="btn-link" (click)="toggleInviteCodeInput()">
              Enter code manually
            </button>
          </div>
          
          <div *ngIf="showInviteCodeInput" class="manual-input">
            <input
              type="text"
              id="inviteCode"
              formControlName="inviteCode"
              class="form-control"
              placeholder="Enter referral code"
              [class.error]="inviteForm.get('inviteCode')?.invalid && inviteForm.get('inviteCode')?.touched">
            <button type="button" class="btn-link" (click)="toggleInviteCodeInput()">
              Select from relatives
            </button>
          </div>
        </div>

        <!-- For Non-Admin: Disabled input with pre-filled code -->
        <input
          *ngIf="!isAdmin"
          type="text"
          id="inviteCode"
          formControlName="inviteCode"
          class="form-control"
          [disabled]="true">
        
        <div *ngIf="inviteForm.get('inviteCode')?.invalid && inviteForm.get('inviteCode')?.touched" class="error-message">
          Invite code is required
        </div>
      </div>

      <!-- Mobile Number -->
      <div class="form-group">
        <label for="mobileNumber" class="form-label">
          Mobile Number <span class="required">*</span>
        </label>
        <input
          type="tel"
          id="mobileNumber"
          formControlName="mobileNumber"
          class="form-control"
          placeholder="Enter 10-digit mobile number"
          [class.error]="inviteForm.get('mobileNumber')?.invalid && inviteForm.get('mobileNumber')?.touched">
        <div *ngIf="inviteForm.get('mobileNumber')?.invalid && inviteForm.get('mobileNumber')?.touched" class="error-message">
          <span *ngIf="inviteForm.get('mobileNumber')?.errors?.['required']">Mobile number is required</span>
          <span *ngIf="inviteForm.get('mobileNumber')?.errors?.['pattern']">Please enter a valid 10-digit mobile number</span>
        </div>
      </div>

      <!-- Message -->
      <div class="form-group">
        <label for="message" class="form-label">
          Message <span class="required">*</span>
        </label>
        <textarea
          id="message"
          formControlName="message"
          class="form-control textarea"
          rows="5"
          placeholder="Invitation message"
          [class.error]="inviteForm.get('message')?.invalid && inviteForm.get('message')?.touched"></textarea>
        <div *ngIf="inviteForm.get('message')?.invalid && inviteForm.get('message')?.touched" class="error-message">
          Message is required
        </div>
      </div>

      <!-- Message Type -->
      <div class="form-group">
        <label class="form-label">
          Message Type <span class="required">*</span>
        </label>
        <div class="message-type-options">
          <label *ngFor="let msgType of messageTypes" class="radio-label" [class.disabled]="!msgType.enabled">
            <input
              type="radio"
              [value]="msgType.value"
              formControlName="messageType"
              [disabled]="!msgType.enabled">
            <span>{{ msgType.label }}</span>
            <span *ngIf="!msgType.enabled" class="coming-soon">(Coming Soon)</span>
          </label>
        </div>
      </div>

      <!-- Success Message -->
      <div *ngIf="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>

      <!-- Error Message -->
      <div *ngIf="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button type="button" class="btn btn-secondary" (click)="onCancel()" [disabled]="isSending">
          Cancel
        </button>
        <button type="submit" class="btn btn-primary" [disabled]="isSending || inviteForm.invalid">
          <span *ngIf="isSending">Sending...</span>
          <span *ngIf="!isSending">Send</span>
        </button>
      </div>
    </form>

    <!-- Referral Links Section (for relative login only) -->
    <div class="referral-links-section" *ngIf="!isAdmin && profileData">
      <h2 class="section-title">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 13A5 5 0 0 0 20 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <path d="M14 17A5 5 0 0 0 4 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <path d="M10 13A3 3 0 0 1 13 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <path d="M14 17A3 3 0 0 1 17 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <line x1="13" y1="10" x2="17" y2="14" stroke="currentColor" stroke-width="2"/>
        </svg>
        Share Referral Links
      </h2>
      <p class="section-description">Share these links with family members to invite them to join your family tree</p>
      
      <div class="referral-cards-grid">
        <!-- Parent Referral Link (for adding children) -->
        <div class="referral-card parent-card">
          <div class="referral-card-header">
            <div class="referral-icon parent-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 21V19A4 4 0 0 0 13 15H5A4 4 0 0 0 1 19V21" stroke="currentColor" stroke-width="2"/>
                <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
                <path d="M23 21V19A4 4 0 0 0 19.5 15.5" stroke="currentColor" stroke-width="2"/>
                <path d="M16 3.13A4 4 0 0 1 16 10.87" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <h3 class="referral-card-title">For Children</h3>
          </div>
          <p class="referral-card-description">Share this link with your children to add them to your family tree</p>
          <div class="referral-link-input-group">
            <input 
              type="text" 
              [value]="parentReferralLink" 
              readonly 
              class="referral-link-input"
              [class.disabled]="!parentReferralLink"
              placeholder="Loading..." />
            <div class="referral-link-actions">
              <button 
                type="button" 
                class="copy-btn"
                (click)="copyToClipboard(parentReferralLink, 'Parent')"
                [disabled]="!parentReferralLink"
                title="Copy link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
                  <path d="M5 15H4A2 2 0 0 1 2 13V4A2 2 0 0 1 4 2H13A2 2 0 0 1 15 4V5" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <button 
                type="button" 
                class="share-btn"
                (click)="shareLink('parent')"
                [disabled]="!parentReferralLink"
                title="Share link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="18" cy="5" r="3" stroke="currentColor" stroke-width="2"/>
                  <circle cx="6" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                  <circle cx="18" cy="19" r="3" stroke="currentColor" stroke-width="2"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke="currentColor" stroke-width="2"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Spouse Referral Link -->
        <div class="referral-card spouse-card">
          <div class="referral-card-header">
            <div class="referral-icon spouse-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="4" stroke="currentColor" stroke-width="2"/>
                <circle cx="16" cy="16" r="4" stroke="currentColor" stroke-width="2"/>
                <path d="M11 11L13 13" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <h3 class="referral-card-title">For Spouse</h3>
          </div>
          <p class="referral-card-description">Share this link with your spouse to connect them to your family tree</p>
          <div class="referral-link-input-group">
            <input 
              type="text" 
              [value]="spouseReferralLink" 
              readonly 
              class="referral-link-input"
              [class.disabled]="!spouseReferralLink"
              placeholder="Loading..." />
            <div class="referral-link-actions">
              <button 
                type="button" 
                class="copy-btn"
                (click)="copyToClipboard(spouseReferralLink, 'Spouse')"
                [disabled]="!spouseReferralLink"
                title="Copy link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
                  <path d="M5 15H4A2 2 0 0 1 2 13V4A2 2 0 0 1 4 2H13A2 2 0 0 1 15 4V5" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <button 
                type="button" 
                class="share-btn"
                (click)="shareLink('spouse')"
                [disabled]="!spouseReferralLink"
                title="Share link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="18" cy="5" r="3" stroke="currentColor" stroke-width="2"/>
                  <circle cx="6" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                  <circle cx="18" cy="19" r="3" stroke="currentColor" stroke-width="2"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke="currentColor" stroke-width="2"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Sibling Referral Link -->
        <div class="referral-card sibling-card">
          <div class="referral-card-header">
            <div class="referral-icon sibling-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
                <path d="M5 21V19A4 4 0 0 1 9 15H15A4 4 0 0 1 19 19V21" stroke="currentColor" stroke-width="2"/>
                <circle cx="8" cy="7" r="3" stroke="currentColor" stroke-width="2"/>
                <circle cx="16" cy="7" r="3" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <h3 class="referral-card-title">For Siblings</h3>
          </div>
          <p class="referral-card-description" *ngIf="siblingReferralLink">
            Share this link with your siblings to add them to your family tree (uses parent's referral code)
          </p>
          <p class="referral-card-description error" *ngIf="!siblingReferralLink">
            Sibling link is only available if you have a parent registered in the system
          </p>
          <div class="referral-link-input-group">
            <input 
              type="text" 
              [value]="siblingReferralLink" 
              readonly 
              class="referral-link-input"
              [class.disabled]="!siblingReferralLink"
              placeholder="Not available - no parent registered" />
            <div class="referral-link-actions">
              <button 
                type="button" 
                class="copy-btn"
                (click)="copyToClipboard(siblingReferralLink, 'Sibling')"
                [disabled]="!siblingReferralLink"
                title="Copy link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
                  <path d="M5 15H4A2 2 0 0 1 2 13V4A2 2 0 0 1 4 2H13A2 2 0 0 1 15 4V5" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <button 
                type="button" 
                class="share-btn"
                (click)="shareLink('sibling')"
                [disabled]="!siblingReferralLink"
                title="Share link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="18" cy="5" r="3" stroke="currentColor" stroke-width="2"/>
                  <circle cx="6" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                  <circle cx="18" cy="19" r="3" stroke="currentColor" stroke-width="2"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke="currentColor" stroke-width="2"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

